/**
 * PHFSplitImage
 *
 * Two-column section with a photo on one side and text content on the other.
 * Text includes a navy title, body paragraph, optional shield-icon bullet list,
 * and an optional disclaimer. The image/text order can be flipped via `imageLeft`.
 *
 * Props:
 * - image (string, required): URL for the photo
 * - title (string, required): Section heading in navy
 * - body (string, required): Paragraph body text in navy
 * - items (string[], optional): List of bullet points with shield icons. Omit to hide the list.
 * - disclaimer (string, optional): Small grey text rendered below the bullet list
 * - imageLeft (boolean, optional): If true, image appears on the left. Defaults to false (text left, image right).
 */
const PHFSplitImage = ({ image, title, body, items, disclaimer, imageLeft = false }) => {
  const textContent = (
    <div className="lg:w-1/2 flex items-center px-12 py-16">
      <div className="max-w-lg">
        <h2 className="!text-[36px] font-bold text-blue-navy font-poppins text-left leading-tight mb-6">
          {title}
        </h2>
        <p className="text-blue-navy text-[16px] text-left leading-relaxed mb-6">
          {body}
        </p>
        {items && items.length > 0 && (
          <ul className="space-y-4 mb-6">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-4">
                <svg
                  className="w-5 h-5 text-blue flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                  />
                </svg>
                <span className="text-blue-navy text-[16px] text-left">{item}</span>
              </li>
            ))}
          </ul>
        )}
        {disclaimer && (
          <p className="text-gray-400 text-[13px] text-left">{disclaimer}</p>
        )}
      </div>
    </div>
  );

  const imageContent = (
    <div className="lg:w-1/2">
      <img src={image} alt={title} className="w-full h-full object-cover" />
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row min-h-[500px]">
      {imageLeft ? imageContent : textContent}
      {imageLeft ? textContent : imageContent}
    </div>
  );
};

export default PHFSplitImage;
